import { ApolloError } from 'apollo-client'
import * as React from 'react'
import { Mutation, MutationFunction } from 'react-apollo'
import { useTranslation } from 'react-i18next'

import { LOCIZE_NAMESPACE } from 'main/common'
import Form, { FormGroup, FormSection } from 'main/components/common/Form'
import { PageContext } from 'main/components/common/Page'
import { AppContext } from 'main/framework'
import { i18nKeyPrefix } from 'main/pages/Dashboard/pages/PaymentSettings/common'
import {
  Button,
  CreditCardTextField,
  DateTextField,
  PostalCodeTextField,
  spacing,
  Strong,
} from 'main/ui'
import { errorAlert, IPageAlert, successAlert } from 'main/util/page'
import { Stripe } from 'main/util/paymentMethod'

import {
  PAYMENT_METHODS_QUERY,
  UPSERT_PAYMENT_METHOD_MUTATION,
  UpsertPaymentMethod,
  UpsertPaymentMethodVariables,
} from 'main/gql/paymentMethod'

import CvcField from './CvcField'
import { IParams, IValidation } from './types'
import { initialParams, validate } from './util'

interface IProps {
  onComplete?: () => void
}

const PRE = `${i18nKeyPrefix}-CREDIT-CARD-FORM`

export default function CardForm(props: IProps) {
  const { onComplete } = props
  const { t, i18n } = useTranslation(LOCIZE_NAMESPACE)
  const { user } = React.useContext(AppContext)
  const { setAlert } = React.useContext(PageContext)

  const [params, setParams] = React.useState<IParams>(initialParams())
  const [validationHints, setValidationHints] = React.useState<IValidation>({})

  const content = React.useMemo(() => {
    return {
      creditCardForm: {
        description: t(`${PRE}-DESCRIPTION`),
        creditCardNumber: t(`${PRE}-CREDIT-CARD-NUMBER`),
        expiration: t(`${PRE}-CREDIT-CARD-EXPIRATION`),
        zipCode: t(`${PRE}-CREDIT-CARD-ZIPCODE`),
        saveBtn: t(`${PRE}-CREDIT-CARD-SAVE-BTN`),
        savingBtn: t(`${PRE}-CREDIT-CARD-SAVING-BTN`),
      },
    }
  }, [i18n.resolvedLanguage])

  return (
    <Mutation<UpsertPaymentMethod, UpsertPaymentMethodVariables>
      mutation={UPSERT_PAYMENT_METHOD_MUTATION}
      onCompleted={() => {
        if (onComplete) {
          onComplete()
        }

        setAlert(successAlert)
      }}
      onError={error => handleError(error, setAlert)}
      refetchQueries={[{ query: PAYMENT_METHODS_QUERY, variables: { userId: user!.id } }]}
    >
      {(mutate, { loading }) => (
        <Form
          onSubmit={() => handleSubmit(params, mutate, setAlert, setParams, setValidationHints)}
        >
          <div css={{ marginBottom: spacing.majorScale(2) }}>
            <Strong>{content.creditCardForm.description}</Strong>
          </div>

          <FormSection>
            <CreditCardTextField
              label={content.creditCardForm.creditCardNumber}
              placeholder="0000 0000 0000 0000"
              validationHint={validationHints.number}
              value={params.number}
              onChange={cardNumber => handleParamsChange({ number: cardNumber }, setParams)}
              required
            />

            <CvcField
              validationHint={validationHints.cvc}
              value={params.cvc}
              onChange={cvc => handleParamsChange({ cvc }, setParams)}
            />

            <FormGroup>
              <DateTextField
                label={content.creditCardForm.expiration}
                cleaveDatePattern={['m', 'y']}
                placeholder="mm/yy"
                validationHint={validationHints.exp}
                value={params.exp}
                onChange={exp => handleParamsChange({ exp }, setParams)}
                required
              />

              <PostalCodeTextField
                label={content.creditCardForm.zipCode}
                placeholder="12345"
                validationHint={validationHints.address_zip}
                value={params.address_zip}
                onChange={addressZip => handleParamsChange({ address_zip: addressZip }, setParams)}
                required
              />
            </FormGroup>

            <Button full disabled={loading}>
              {loading ? content.creditCardForm.savingBtn : content.creditCardForm.saveBtn}
            </Button>
          </FormSection>
        </Form>
      )}
    </Mutation>
  )
}

function handleParamsChange(
  changes: Partial<IParams>,
  setParams: React.Dispatch<React.SetStateAction<IParams>>,
) {
  setParams(params => ({ ...params, ...changes }))
}

function handleSubmit(
  params: IParams,
  mutate: MutationFunction<UpsertPaymentMethod, UpsertPaymentMethodVariables>,
  setAlert: (alert: IPageAlert | null) => void,
  setParams: React.Dispatch<React.SetStateAction<IParams>>,
  setValidationHints: React.Dispatch<React.SetStateAction<IValidation>>,
) {
  const validationHints = validate(params)
  if (validationHints) return setValidationHints(validationHints)

  setValidationHints({})

  Stripe.createCreditCard(params)
    .then((variables: UpsertPaymentMethodVariables) => mutate({ variables }))
    .catch((errors: [string]) => {
      const errorMessage = errors[0]
      // Clearing expiration date since it needs to be re-entered after an error
      handleParamsChange({ exp: '' }, setParams)
      return errorMessage ? setAlert({ body: errorMessage, kind: 'danger' }) : setAlert(errorAlert)
    })
}

function handleError(error: ApolloError, setAlert: (alert: IPageAlert | null) => void) {
  const errorMessage = error.graphQLErrors[0] && error.graphQLErrors[0].message
  return errorMessage ? setAlert({ body: errorMessage, kind: 'danger' }) : setAlert(errorAlert)
}
